// F-067 : occupants, quotes-parts, avenants (PDF, signature hors Bailivo, effets datés) et accès délégués du bail.
import { useState } from "react";
import { post } from "@core/api";
import { useGet, useInvalidate } from "@core/hooks";
import { date, money } from "@core/format";
import { useOrg } from "@core/org";
import { Badge, Button, Card, ErrorBox, FileDrop, Input, Loading, Notice, Select, Table, Textarea, fieldError } from "@core/ui";
import { AMENDMENT_KINDS, AMENDMENT_STATUS, RuleNote, Status, docUrl, options, orgApi, sendFile, todayIso, useAction } from "./shared";
import { LandlordDelegations } from "./delegations";

export function LeaseAmendmentsTab({ orgId, leaseId }: { orgId: string; leaseId: string }) {
  const org = useOrg();
  const partiesPath = orgApi(orgId, `/leases/${leaseId}/parties`);
  const listPath = orgApi(orgId, `/leases/${leaseId}/amendments`);
  const parties = useGet<any>(partiesPath);
  const list = useGet<any>(listPath);
  const invalidate = useInvalidate();
  const refresh = () => invalidate(partiesPath, listPath);
  const [creating, setCreating] = useState(false);
  const act = useAction();
  const [sign, setSign] = useState<{ id: string; signed_on: string; doc: { id: string; filename: string } | null } | null>(null);
  if (parties.isLoading || list.isLoading) return <Loading />;
  if (parties.error) return <ErrorBox error={parties.error} />;
  const p = parties.data;
  const tenants = p.parties.filter((x: any) => x.role === "locataire" && x.active);
  return (
    <div className="stack">
      <ErrorBox error={act.error} />
      <Card title="Occupants et quotes-parts">
        <Table caption="Parties au bail">
          <thead>
            <tr>
              <th>Personne</th>
              <th>Rôle</th>
              <th className="right">Quote-part</th>
              <th>Arrivée</th>
              <th>Sortie</th>
            </tr>
          </thead>
          <tbody>
            {p.parties.map((x: any) => (
              <tr key={x.id}>
                <td>
                  {x.full_name}
                  {!x.active ? <Badge>Sorti</Badge> : null}
                </td>
                <td>{x.role}</td>
                <td className="vie-amount">{x.share_pct != null ? `${x.share_pct.toLocaleString("fr-FR")} %` : "—"}</td>
                <td className="num">{date(x.joined_on)}</td>
                <td className="num">{date(x.left_on)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
        {p.rent_split.length > 1 ? (
          <p className="small">
            Répartition du loyer et des charges : {p.rent_split.map((r: any) => `${r.full_name} ${money(r.rent_cents)} + ${money(r.charges_cents)}`).join(" · ")}
            {!p.shares_complete ? " (parts égales faute de quotes-parts complètes)" : ""}
          </p>
        ) : null}
        <p className="small muted">{p.rappel}</p>
      </Card>
      <Card title="Avenants" actions={org.can("operate") ? <Button small variant="secondaire" onClick={() => setCreating(!creating)}>{creating ? "Fermer" : "Préparer un avenant"}</Button> : null}>
        <Notice tone="info">{list.data?.signature?.message}</Notice>
        {creating ? (
          <AmendmentForm
            orgId={orgId}
            leaseId={leaseId}
            tenants={tenants}
            onDone={() => {
              setCreating(false);
              refresh();
            }}
          />
        ) : null}
        {list.data?.items.length ? (
          <Table caption="Avenants">
            <thead>
              <tr>
                <th>Avenant</th>
                <th>Effet</th>
                <th>Statut</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {list.data.items.map((a: any) => (
                <tr key={a.id}>
                  <td>
                    {a.title}
                    <span className="block small muted">{AMENDMENT_KINDS[a.kind]}</span>
                    {a.changes?.solidarity?.text ? <RuleNote version={a.changes.rule_version}>{a.changes.solidarity.text}</RuleNote> : null}
                  </td>
                  <td className="num">{date(a.effective_on)}</td>
                  <td>
                    <Status value={a.status} labels={AMENDMENT_STATUS} />
                    {a.signed_on ? <span className="block small muted">signé le {date(a.signed_on)}</span> : null}
                  </td>
                  <td>
                    <div className="row">
                      {a.document_id ? (
                        <a className="small" href={docUrl(a.signed_document_id ?? a.document_id)} target="_blank" rel="noreferrer">
                          PDF
                        </a>
                      ) : null}
                      {org.can("operate") && ["brouillon", "a_signer"].includes(a.status) ? (
                        <>
                          <Button small variant="secondaire" onClick={async () => { await act.run(() => post(orgApi(orgId, `/amendments/${a.id}/document`), {}), "PDF de l’avenant édité."); refresh(); }}>
                            {a.document_id ? "Rééditer le PDF" : "Éditer le PDF"}
                          </Button>
                          <Button small variant="discret" onClick={async () => { await act.run(() => post(orgApi(orgId, `/amendments/${a.id}/cancel`), {}), "Avenant annulé."); refresh(); }}>
                            Annuler
                          </Button>
                        </>
                      ) : null}
                      {org.can("decide") && a.status === "a_signer" ? (
                        <Button small onClick={() => setSign({ id: a.id, signed_on: todayIso(), doc: null })}>
                          Enregistrer la signature
                        </Button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          <p className="muted">Aucun avenant.</p>
        )}
        {sign ? (
          <form
            className="stack-sm"
            onSubmit={async (e) => {
              e.preventDefault();
              if (await act.run(() => post(orgApi(orgId, `/amendments/${sign.id}/sign`), { signed_on: sign.signed_on, signed_document_id: sign.doc?.id }), "Avenant signé : effets appliqués à la date d’effet.")) {
                setSign(null);
                refresh();
              }
            }}
          >
            <h3>Signature de l’avenant</h3>
            <Input label="Signé le" type="date" value={sign.signed_on} error={fieldError(act.error, "signed_on")} onChange={(e) => setSign({ ...sign, signed_on: e.target.value })} />
            <FileDrop
              label={sign.doc ? `Version signée : ${sign.doc.filename}` : "Déposer la version signée"}
              onFiles={async ([f]) => {
                const r = await act.run(() => sendFile(`/api/v1/orgs/${orgId}/documents`, f, { lease_id: leaseId, category: "avenant_signe" }));
                if (r) setSign({ ...sign, doc: { id: r.id, filename: r.filename } });
              }}
            />
            <p className="small muted">Les effets (sortie datée, arrivée, quotes-parts) s’appliquent à la date d’effet. L’historique des occupants est conservé.</p>
            <div className="row">
              <Button type="submit" busy={act.busy}>
                Confirmer la signature
              </Button>
              <Button variant="discret" onClick={() => setSign(null)}>
                Fermer
              </Button>
            </div>
          </form>
        ) : null}
      </Card>
      <LandlordDelegations orgId={orgId} leaseId={leaseId} canManage={org.can("members")} />
    </div>
  );
}

function AmendmentForm({ orgId, leaseId, tenants, onDone }: { orgId: string; leaseId: string; tenants: any[]; onDone: () => void }) {
  const [f, setF] = useState({ kind: "sortie_colocataire", effective_on: todayIso(), leaving_party_id: "", new_name: "", new_email: "", new_phone: "", description: "" });
  const [shares, setShares] = useState<Record<string, string>>({});
  const [result, setResult] = useState<any>(null);
  const act = useAction();
  const leaving = ["sortie_colocataire", "remplacement_colocataire"].includes(f.kind);
  const arriving = ["entree_colocataire", "remplacement_colocataire"].includes(f.kind);
  const after = [...tenants.filter((t) => t.id !== f.leaving_party_id).map((t) => ({ key: t.id, name: t.full_name })), ...(arriving ? [{ key: "nouveau", name: f.new_name || "Nouveau colocataire" }] : [])];
  const showShares = ["sortie_colocataire", "entree_colocataire", "remplacement_colocataire", "quotes_parts"].includes(f.kind);
  return (
    <form
      className="stack"
      onSubmit={async (e) => {
        e.preventDefault();
        const shareList = after.filter((a) => shares[a.key]).map((a) => ({ party_id: a.key === "nouveau" ? null : a.key, share_pct: Number(shares[a.key].replace(",", ".")) }));
        const payload: any = { kind: f.kind, effective_on: f.effective_on, description: f.description, shares: shareList };
        if (leaving) payload.leaving_party_id = f.leaving_party_id || undefined;
        if (arriving) payload.new_party = { full_name: f.new_name, email: f.new_email || undefined, phone: f.new_phone || undefined };
        const r = await act.run(() => post(orgApi(orgId, `/leases/${leaseId}/amendments`), payload), "Avenant préparé.");
        if (r) {
          setResult(r);
          onDone();
        }
      }}
    >
      <ErrorBox error={act.error} />
      <div className="form-row">
        <Select label="Modification" value={f.kind} options={options(AMENDMENT_KINDS)} onChange={(e) => setF({ ...f, kind: e.target.value })} />
        <Input label="Date d’effet" type="date" value={f.effective_on} error={fieldError(act.error, "effective_on")} onChange={(e) => setF({ ...f, effective_on: e.target.value })} />
      </div>
      {leaving ? (
        <Select
          label="Colocataire qui part"
          value={f.leaving_party_id}
          error={fieldError(act.error, "leaving_party_id")}
          onChange={(e) => setF({ ...f, leaving_party_id: e.target.value })}
          options={[{ value: "", label: "Choisir…" }, ...tenants.map((t) => ({ value: t.id, label: t.full_name }))]}
        />
      ) : null}
      {arriving ? (
        <div className="form-row">
          <Input label="Nouveau colocataire" required value={f.new_name} error={fieldError(act.error, "new_party.full_name")} onChange={(e) => setF({ ...f, new_name: e.target.value })} />
          <Input label="Adresse e-mail" type="email" value={f.new_email} error={fieldError(act.error, "new_party.email")} onChange={(e) => setF({ ...f, new_email: e.target.value })} />
          <Input label="Téléphone" value={f.new_phone} onChange={(e) => setF({ ...f, new_phone: e.target.value })} />
        </div>
      ) : null}
      {showShares ? (
        <fieldset className="stack-sm">
          <legend>Quotes-parts après l’avenant (en %, total 100 %)</legend>
          <div className="form-row">
            {after.map((a) => (
              <Input key={a.key} label={a.name} inputMode="decimal" value={shares[a.key] ?? ""} onChange={(e) => setShares({ ...shares, [a.key]: e.target.value })} />
            ))}
          </div>
          {fieldError(act.error, "shares") ? (
            <p className="bv-champ-aide" role="alert">
              {fieldError(act.error, "shares")}
            </p>
          ) : (
            <p className="small muted">Laissez vide pour conserver les quotes-parts actuelles.</p>
          )}
        </fieldset>
      ) : null}
      <Textarea label="Description" rows={3} value={f.description} error={fieldError(act.error, "description")} onChange={(e) => setF({ ...f, description: e.target.value })} />
      {leaving ? (
        <RuleNote>Si le bail contient une clause de solidarité, le colocataire sortant et son garant peuvent rester tenus jusqu’à l’arrivée d’un remplaçant figurant au bail, et au plus tard six mois après la date d’effet de son départ.</RuleNote>
      ) : null}
      {result?.solidarity ? <Notice tone="info">{result.solidarity.text}</Notice> : null}
      <div>
        <Button type="submit" busy={act.busy}>
          Préparer l’avenant
        </Button>
      </div>
    </form>
  );
}
